import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { Accordion, FormCard } from '../../../../components';
import type { AccordionDataItem } from '../../../../components/Accordion/types';
import { logger } from '../../../../features/logger';
import { filterValidRequests } from '../../../../utils/requestFilterHelper';
import type { TFunc } from '../../types';
import RequestCard from '../RequestCard';

/** A customer onboarding request as rendered in the dashboard list. */
export interface CustomerRequestData {
  id?: string;
  recordType?: string;
  address?: { address?: string; name?: string };
  region?: string;
  countryCode?: string;
  business?: string;
  status?: string | null;
}

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding-left: var(--spacing-4xl, 3rem);
  padding-right: var(--spacing-4xl, 3rem);

  /* At mobile and below, remove padding entirely */
  @media only screen and (max-width: 700px) {
    padding: 0;
  }
`;

const AccordionTitle = styled.p`
  font: var(--sprout-font-h5);
  color: var(--neutral-500, #707773);
  @media only screen and (max-width: 700px) {
    font: var(--sprout-font-h6);
  }
`;

const FontBold = styled.span`
  color: var(--neutral-1000, #101c16);
  font: var(--sprout-font-h5);
  @media only screen and (max-width: 700px) {
    font: var(--sprout-font-h6);
  }
`;
const Wrapper = styled.div`
  width: 100%;
  user-select: none;
  margin-top: var(--spacing-md);
`;

interface CustomerRequestProps {
  t: TFunc;
  key?: string;
  requests: CustomerRequestData[];
}

function CustomerRequest({ t, key, requests }: CustomerRequestProps) {
  const getConfig = (request: CustomerRequestData) => ({
    canHaveMultipleOpen: true,
    request: request,
    title: (
      <AccordionTitle>
        <FontBold>{` ${t('dashboard.actions.ID')}: ${request.id}`}</FontBold>
        {` - ${request.recordType}`}
      </AccordionTitle>
    ),
    body: <RequestCard {...request} />
  });

  return (
    <Accordion
      key={key}
      canHaveMultipleOpen
      canRunInitialOpenTabs
      customTitle
      fullWidth
      data={filterValidRequests(requests).map(request => getConfig(request)) as unknown as AccordionDataItem[]}
    />
  );
}

interface CustomerRequestCardProps {
  requests: CustomerRequestData[];
  contactName?: string;
}

function CustomerRequestCard({ requests, contactName = '' }: CustomerRequestCardProps) {
  const { t } = useTranslation();

  if (!requests.length) {
    logger.info(`RED-CARPET-FE NO Customer Request Found.`);
  }

  const cardProps = {
    cardId: 'dashboard',
    title: requests.length === 1 ? t('dashboard.singleRequest.title') : t('dashboard.title'),
    subtitle: requests.length === 1 ? t('dashboard.singleRequest.subtitle') : t('dashboard.subtitle'),
    image: '/food/onboarding/assets/Dashboard.svg',
    t,
    customWidth: '100%'
  };

  const greetingMessage = `${t('dashboard.greeting')}, ${
    contactName ? contactName.charAt(0).toUpperCase() + contactName.slice(1) : ''
  }`;

  return (
    <ContentWrapper>
      <FormCard {...cardProps} welcomeTitle={greetingMessage}>
        <Wrapper>
          <CustomerRequest t={t} requests={requests} />
        </Wrapper>
      </FormCard>
    </ContentWrapper>
  );
}

export default CustomerRequestCard;
